import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { KeyRound, Loader2, Send, UserX } from "lucide-react";

export function PortalAccessCard({ participant }) {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.portalAccess(participant).then(setRows).catch(() => setRows([]));
  useEffect(() => { setRows(null); load(); /* eslint-disable-next-line */ }, [participant]);

  const grant = async (r) => {
    setBusy(r.email);
    try { await api.grantPortalAccess(participant, { email: r.email, name: r.name }); toast.success(`Portal invite sent to ${r.email}.`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); } finally { setBusy(""); }
  };
  const revoke = async (r) => {
    if (!window.confirm(`Revoke portal access for ${r.name} (${r.email})?`)) return;
    setBusy(r.email);
    try { await api.revokePortalAccess(participant, r.email); toast.success("Access revoked."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); } finally { setBusy(""); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="portal-access-card">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5 text-slate-500" />Participant portal access</div>
      <p className="text-[11px] text-slate-500 mb-3">Give the participant and/or their linked contacts a portal login to see the roster and sign documents. Only people with an email on record appear here.</p>
      {rows === null ? <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div> : (
        <div className="divide-y divide-slate-50">
          {rows.length === 0 && <div className="text-xs text-slate-400 py-2">Add an email to the participant or a contact to enable portal access.</div>}
          {rows.map((r) => (
            <div key={r.key} className="flex items-center gap-3 py-2" data-testid={`portal-access-${r.key}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-700 truncate">{r.name} <span className="text-[10px] text-slate-400">· {r.relationship}</span></div>
                <div className="text-[10px] text-slate-400 truncate">{r.email}</div>
              </div>
              {r.has_access ? <Badge className="text-[9px] py-0 bg-emerald-50 text-emerald-700 border-emerald-200">Access on</Badge>
                            : <Badge className="text-[9px] py-0 bg-slate-100 text-slate-400 border-slate-200">No access</Badge>}
              {can.has("participants.portal_access") && (r.has_access
                ? <Button size="sm" variant="ghost" className="h-7 text-[11px] text-rose-600" onClick={() => revoke(r)} disabled={busy === r.email} data-testid={`portal-revoke-${r.key}`}><UserX className="h-3.5 w-3.5 mr-1" />Revoke</Button>
                : <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => grant(r)} disabled={busy === r.email} data-testid={`portal-grant-${r.key}`}>{busy === r.email ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" />Invite</>}</Button>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
